'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';

const CALENDLY_URL = 'https://calendly.com/sai-krishna-paid-ads-marketer/new-meeting';

// ─── Original copy (Claude will rewrite these) ────────────────────────────────
const ORIGINAL_COPY = {
  heroHeadline: "Your Ads Are Burning Cash. Let\'s Fix That.",
  heroSub: "We help D2C brands spending $10K–$50K/month on ads stop bleeding money and start scaling profitably. Meta Ads, Google Ads, Retention Marketing, a full-funnel growth.",
  heroTrust: "No retainers until we prove the ROI.",
  painTitle: "Does These Problems Sound Familiar?",
  pain1Title: "Ad Spend Climbing, ROAS Falling",
  pain1Body: "You're spending $10K–$50K/month but cost per acquisition keeps rising while returns shrink.",
  pain2Title: "Leaky Funnel, No Diagnosis",
  pain2Body: "Traffic looks decent but conversions are stuck. Nobody's told you if it's the page, the offer, or the audience.",
  pain3Title: "Tracking Is a Mess",
  pain3Body: "Meta says one thing, GA4 says another, Shopify says something else. You're making decisions on broken data.",
  pain4Title: "Agency Fatigue",
  pain4Body: "Big promises, recycled playbooks, zero accountability. You've been through 2–3 agencies already.",
  painCta: "If you nodded at even one of these — we should talk.",
  howTitle: "Stranger to Scaling Partner in 21 Days",
  howSub: "No surprises. No black boxes. Here\'s exactly what happens.",
  step1Title: "Discovery Call",
  step1Day: "Day 1",
  step1Desc: "45 minutes. No pitch deck. We ask what's working, what's broken, and what good looks like. If we're not the right fit, we'll say so.",
  step2Title: "Free Account Audit",
  step2Day: "Days 2–4",
  step2Desc: "Before you pay us a dollar, we dig into your Meta, Google, GA4, and Shopify. You get a written audit with 3–5 actionable findings — free, no strings.",
  step3Title: "Proposal & Alignment",
  step3Day: "Days 4–5",
  step3Desc: "Clear proposal: what we'll do, what it costs, what to expect in 30/60/90 days. We define KPIs together. If the numbers work for both sides, we move forward.",
  step4Title: "Onboarding & Access",
  step4Day: "Days 5–7",
  step4Desc: "Simple onboarding form. Secure access collection. No spreadsheets full of passwords.",
  step5Title: "Fix Tracking First (if needed)",
  step5Day: "Days 7–10",
  step5Desc: "Before touching campaigns, we fix measurement. Meta Pixel, Conversions API, GA4, GTM. If tracking is broken, every optimization is a guess.",
  step6Title: "Campaign Architecture",
  step6Day: "Days 10–14",
  step6Desc: "We build or restructure your campaign structure. Audience segmentation, budget allocation, creative roadmap. You approve before anything goes live.",
  step7Title: "Launch & Optimize",
  step7Day: "Days 14–21",
  step7Desc: "Campaigns live. Daily monitoring, weekly optimization, bi-weekly calls. Structured experiments. We scale what wins.",
  step8Title: "Scale or Kill",
  step8Day: "Ongoing",
  step8Desc: "Monthly review. Winners get more budget. Losers get cut. We make your ad spend the most profitable line item on your P&L.",
  whyTitle: "Why Brands Choose Us",
  why1Title: "We are Operators, Not Pitch Deck People",
  why1Body: "Most agencies live inside Ads Manager. We look at the full picture where users drop off, why they don't convert, what they say when they leave, exit surveys, funnel analysis then we strategize the campaigns.",
  why2Title: "We Find What\'s Broken Before You Pay Us.",
  why2Body: "Every engagement starts with a free audit. No strings, no pitch - just clarity on what's bleeding money and why.",
  why3Title: "We Spend Your Budget Like It\'s Ours.",
  why3Body: "No inflated spends to hit targets. No running campaigns we know aren't working. Every dollar is a decision we'd make like its our own money on the line.",
  ctaHeadline: "Are Your Ads Leaving Money on the Table?",
  ctaSub: "You'll speak with a D2C marketing expert, not a sales rep. We're here to provide insights and discuss your growth goals from day one.",
  ctaExpert: "You'll speak with a D2C marketing expert, not a sales rep. We're here to provide insights and discuss your growth goals from day one.",
};

// ─── Intersection Observer Hook ───────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ─── Brand Logos for Case Studies ────────────────────────────────────────────
const BRAND_LOGOS = [
  { name: 'Shopify', logo: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg', color: '#96BF48' },
  { name: 'Meta', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg', color: '#0082FB' },
  { name: 'Google Ads', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg', color: '#4285F4' },
  { name: 'WooCommerce', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/WooCommerce_logo.svg', color: '#96588A' },
  { name: 'Google Tag Manager', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/46/Klaviyo_logo.svg', color: '#000000' },
  { name: 'TikTok', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/TikTok_logo.svg', color: '#010101' },
];

// ─── Case Study Brand Logos ───────────────────────────────────────────────────
const CASE_STUDY_BRANDS = [
  {
    id: '1',
    name: 'CoralHaze',
    logoUrl: '',
    fallbackColor: '#E8A598',
    metric: '38% ROAS Boost',
    category: 'D2C Footwear',
    channels: ['Meta Ads', 'CRO'],
    result: 'Fixed ad account structure, optimized checkout UX, and scaled creative A/B tests — delivered $135K ARR with 38% ROAS uptick and 45% AOV growth in 6 months.',
    notionUrl: 'https://www.notion.so/008034ffae4a4aeaaea6cd88505ed2cf',
  },
  {
    id: '2',
    name: 'Tocco',
    logoUrl: '',
    fallbackColor: '#7C5C3E',
    metric: '102% ROAS Boost',
    category: 'Social Commerce / Food',
    channels: ['Meta Ads', 'D2C'],
    result: 'Guided creative strategy with industry-best angles, saving 80% experimentation time. Delivered 102% ROAS boost, 55% CAC drop, and 757% revenue growth in 8 months — zero offers, 100% prepaid.',
    notionUrl: 'https://www.notion.so/8cd6928f234643a39cfdf0d0d994745e',
  },
  {
    id: '3',
    name: 'Soqo',
    logoUrl: '',
    fallbackColor: '#9B59B6',
    metric: '120% ROAS Increase',
    category: 'D2C Lingerie',
    channels: ['Meta Ads', 'Facebook Ads'],
    result: 'Fixed broken pixel tracking, rebuilt landing page, restructured ad account hygiene. Achieved 120% ROAS increase, 52% CAC reduction, and 20% AOV growth over 8 months.',
    notionUrl: 'https://www.notion.so/b8a502744c294656aa077ef9fcc4ce4c',
  },
  {
    id: '4',
    name: 'Happi Mobiles',
    logoUrl: '',
    fallbackColor: '#F39C12',
    metric: '$1M ARR',
    category: 'Mobile Retail Chain',
    channels: ['Meta Ads', 'E-commerce'],
    result: 'Identified checkout friction via heatmaps, revamped product page copy, and scaled winning catalogs with cost caps and CBOs - achieved $1M ARR and 17% conversion boost in 8 months.',
    notionUrl: 'https://www.notion.so/f49e977a8b75462994eb710af450c860',
  },
];

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm' : 'bg-white'}`}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
        <div className="flex items-center">
          <div className="bg-[#0f0f0f] rounded-lg p-1.5">
            <Image src="/assets/images/TheDesignTurf_Logo_PNG-1775227555414.png" alt="The Design Turf" width={110} height={32} className="h-8 w-auto object-contain" priority />
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-10">
          {[['How We Work', 'how-we-work'], ['Case Studies', 'case-studies'], ['Why Us', 'why-us']].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} className="text-[13px] font-semibold text-[#1a1a1a] hover:text-[#7C3AED] transition-colors tracking-wide font-sans">
              {label}
            </button>
          ))}
        </nav>

        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a1a1a] hover:bg-[#7C3AED] text-white text-[13px] font-bold rounded-full transition-all duration-300 tracking-wide">
          Book Free Audit
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
        </a>

        <button className="md:hidden p-2 text-[#1a1a1a]" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-5 flex flex-col gap-5">
          {[['How We Work', 'how-we-work'], ['Case Studies', 'case-studies'], ['Why Us', 'why-us']].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} className="text-left text-[15px] font-semibold text-[#1a1a1a]">{label}</button>
          ))}
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-5 py-3 bg-[#1a1a1a] text-white text-[13px] font-bold rounded-full">Book Free Audit</a>
        </div>
      )}
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function Hero({ copy }: { copy: typeof ORIGINAL_COPY }) {
  const { ref, inView } = useInView(0.1);
  const heroRef = useRef<HTMLElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!bgRef.current) return;
      const scrollY = window.scrollY;
      bgRef.current.style.transform = `translateY(${scrollY * 0.4}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={heroRef} className="pt-[68px] bg-white overflow-hidden relative">
      {/* Parallax background layer */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none will-change-transform" style={{ zIndex: 0 }}>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.04] bg-[#7C3AED] blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.03] bg-[#7C3AED] blur-3xl" />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative z-10">
        {/* Top bar */}
        <div className="flex items-center justify-between py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[11px] font-semibold text-gray-500 tracking-widest uppercase">Accepting New Clients</span>
          </div>
        </div>

        {/* Hero content */}
        <div ref={ref} className={`py-16 md:py-24 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid lg:grid-cols-[1fr_420px] gap-16 items-center">
            <div>
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EDE9FE] rounded-full mb-8">
                <span className="text-[11px] font-bold text-[#7C3AED] tracking-widest uppercase">Performance Marketing</span>
              </div>

              <h1 className="font-display font-black text-[#0f0f0f] leading-[0.92] tracking-tight mb-8" style={{ fontSize: 'clamp(3rem, 6vw, 5.5rem)' }}>
                {copy.heroHeadline}
              </h1>

              <p className="text-[1.1rem] text-gray-500 leading-relaxed max-w-[520px] mb-10 font-light">
                {copy.heroSub}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full transition-all duration-300 text-[15px] shadow-lg shadow-purple-200 hover:shadow-purple-300 hover:-translate-y-0.5">
                  Book Your Free Audit
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                <button onClick={() => document.getElementById('case-studies')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-8 py-4 border-2 border-gray-200 hover:border-[#1a1a1a] text-[#1a1a1a] font-bold rounded-full transition-all duration-300 text-[15px]">
                  See Results
                </button>
              </div>

              <p className="text-[12px] text-gray-400 mt-5 font-medium">{copy.heroTrust}</p>
            </div>

            {/* Stats panel */}
            <div className="hidden lg:block">
              <div className="bg-[#0f0f0f] rounded-3xl p-8 space-y-6">
                <div className="flex items-center gap-3 pb-5 border-b border-white/10">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[11px] font-bold text-white/40 tracking-widest uppercase">Live Results</span>
                </div>
                {[
                  { label: 'Avg. ROAS Delivered', value: '5.8×', change: '+34%', color: '#7C3AED' },
                  { label: 'CAC Reduction', value: '47%', change: 'avg', color: '#10b981' },
                  { label: 'Ad Spend Managed', value: '$4.2M', change: 'monthly', color: '#f59e0b' },
                  { label: 'Revenue Generated', value: '$28.6M', change: 'total', color: '#3b82f6' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-white/40 font-medium mb-1">{stat.label}</div>
                      <div className="text-2xl font-black text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{stat.value}</div>
                    </div>
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: `${stat.color}20`, color: stat.color }}>{stat.change}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Logo ticker */}
        <div className="py-8 border-t border-gray-100">
          <p className="text-[11px] font-bold text-gray-400 tracking-widest uppercase mb-6 text-center">Platforms We Master</p>
          <div className="flex items-center justify-center flex-wrap gap-8 md:gap-12">
            {['Meta', 'Google Ads', 'TikTok', 'Shopify', 'GA4', 'Google Tag Manager'].map((p) => (
              <span key={p} className="text-[13px] font-bold text-gray-300 hover:text-gray-600 transition-colors cursor-default">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pain Points ──────────────────────────────────────────────────────────────
function PainPoints({ copy }: { copy: typeof ORIGINAL_COPY }) {
  const { ref, inView } = useInView();

  const pains = [
    { title: copy.pain1Title, body: copy.pain1Body, icon: '↑$', accent: '#ef4444' },
    { title: copy.pain2Title, body: copy.pain2Body, icon: '⌀', accent: '#f59e0b' },
    { title: copy.pain3Title, body: copy.pain3Body, icon: '≠', accent: '#3b82f6' },
    { title: copy.pain4Title, body: copy.pain4Body, icon: '✗', accent: '#8b5cf6' },
  ];

  return (
    <section id="pain-points" className="bg-[#f8f7ff] py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div>
              <h2 className="font-display font-black text-[#0f0f0f] leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
                {copy.painTitle}
              </h2>
            </div>
            <p className="text-gray-500 max-w-sm leading-relaxed text-[15px]">{copy.painCta}</p>
          </div>

          {/* Bento grid — stagger animations */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {pains.map((pain, i) => (
              <div key={i}
                className={`group relative bg-[#f8f7ff] hover:bg-[#EDE9FE] rounded-2xl p-6 transition-all duration-500 cursor-default ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${i * 120}ms`, transitionProperty: 'opacity, transform, box-shadow, border-color' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black mb-5 transition-all duration-300 group-hover:scale-110"
                  style={{ background: `${pain.accent}15`, color: pain.accent }}>
                  {pain.icon}
                </div>
                <h3 className="font-display font-bold text-[#0f0f0f] text-[1rem] mb-3 leading-snug">{pain.title}</h3>
                <p className="text-gray-500 text-[0.88rem] leading-relaxed">{pain.body}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#0f0f0f] hover:bg-[#7C3AED] text-white font-bold rounded-full transition-all duration-300 text-[14px]">
              Let&apos;s Fix This →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How We Work ─────────────────────────────────────────────────────────────
function HowWeWork({ copy }: { copy: typeof ORIGINAL_COPY }) {
  const { ref, inView } = useInView();

  const steps = [
    { num: '01', title: copy.step1Title, day: copy.step1Day, desc: copy.step1Desc },
    { num: '02', title: copy.step2Title, day: copy.step2Day, desc: copy.step2Desc },
    { num: '03', title: copy.step3Title, day: copy.step3Day, desc: copy.step3Desc },
    { num: '04', title: copy.step4Title, day: copy.step4Day, desc: copy.step4Desc },
    { num: '05', title: copy.step5Title, day: copy.step5Day, desc: copy.step5Desc },
    { num: '06', title: copy.step6Title, day: copy.step6Day, desc: copy.step6Desc },
    { num: '07', title: copy.step7Title, day: copy.step7Day, desc: copy.step7Desc },
    { num: '08', title: copy.step8Title, day: copy.step8Day, desc: copy.step8Desc },
  ];

  return (
    <section id="how-we-work" className="bg-white py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-16">
            <span className="text-[11px] font-bold text-[#7C3AED] tracking-widest uppercase block mb-4">The Process</span>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <h2 className="font-display font-black text-[#0f0f0f] leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
                {copy.howTitle}
              </h2>
              <p className="text-gray-500 max-w-sm text-[15px] leading-relaxed">{copy.howSub}</p>
            </div>
          </div>

          {/* Steps — asymmetric grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, i) => (
              <div key={i}
                className={`group relative bg-[#f8f7ff] hover:bg-[#EDE9FE] rounded-2xl p-6 transition-all duration-300 cursor-default ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[11px] font-black text-[#7C3AED] tracking-widest">{step.num}</span>
                  <span className="text-[10px] font-bold text-gray-400 bg-white px-2.5 py-1 rounded-full">{step.day}</span>
                </div>
                <h3 className="font-display font-bold text-[#0f0f0f] text-[0.95rem] mb-3 leading-snug">{step.title}</h3>
                <p className="text-gray-500 text-[0.82rem] leading-relaxed">{step.desc}</p>
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7C3AED] rounded-b-2xl scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full transition-all duration-300 text-[14px] shadow-lg shadow-purple-200">
              Book Your Discovery Call →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Animated Counter ─────────────────────────────────────────────────────────
function AnimatedCounter({ value, suffix = '', prefix = '' }: { value: number; suffix?: string; prefix?: string }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView(0.3);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (inView && !hasAnimated.current) {
      hasAnimated.current = true;
      const duration = 1800;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [inView, value]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

// ─── Stats Section ────────────────────────────────────────────────────────────
function StatsSection() {
  const { ref, inView } = useInView(0.2);

  const stats = [
    { prefix: '$', value: 28.6, suffix: 'M', label: 'Revenue Generated', decimals: true },
    { prefix: '', value: 47, suffix: '%', label: 'Avg CAC Reduction' },
    { prefix: '$', value: 4.2, suffix: 'M', label: 'Monthly Spend Managed', decimals: true },
    { prefix: '', value: 80, suffix: '+', label: 'Brands Worked With' },
  ];

  return (
    <section className="bg-[#0f0f0f] py-16">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="text-[2.8rem] font-black text-white leading-none mb-2 font-display">
                {stat.prefix}
                <AnimatedCounter value={stat.decimals ? stat.value * 10 : stat.value} />
                {stat.decimals ? '' : stat.suffix}
                {stat.decimals && <span className="text-[1.8rem]">{stat.suffix}</span>}
              </div>
              <div className="text-[11px] font-bold text-gray-500 tracking-widest uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Case Studies ─────────────────────────────────────────────────────────────
interface CaseStudyData {
  id: string;
  title: string;
  metric: string;
  summary: string;
  tags: string[];
  url: string;
}

const FALLBACK_CASE_STUDIES: CaseStudyData[] = CASE_STUDY_BRANDS.map(b => ({
  id: b.id,
  title: b.name,
  metric: b.metric,
  summary: b.result,
  tags: b.channels,
  url: b.notionUrl,
}));

function mapNotionToCaseStudy(page: { id: string; title: string; url: string; tags?: string[] }): CaseStudyData {
  const title = page.title || 'Case Study';
  let metric = '';
  const roasMatch = title.match(/(\d+[\d.]*%?\s*(?:ROAS|roas)[\s\w]*)/i);
  const cacMatch = title.match(/(\d+[\d.]*%?\s*(?:CAC|cac|cost per lead)[\s\w]*)/i);
  const revenueMatch = title.match(/(\d+[\d.]*\s*(?:Million|million|Cr|L|K)\s*(?:ARR|MRR|Revenue|revenue)?)/i);
  if (roasMatch) metric = roasMatch[1].trim();
  else if (cacMatch) metric = cacMatch[1].trim();
  else if (revenueMatch) metric = revenueMatch[1].trim();
  else metric = title.split(':')[0].trim().substring(0, 30);
  return {
    id: page.id,
    title: title.includes(':') ? title.split(':').slice(1).join(':').trim() : title,
    metric,
    summary: title.length > 120 ? title.substring(0, 117) + '...' : title,
    tags: page.tags || [],
    url: `https://www.notion.so/${page.id.replace(/-/g, '')}`,
  };
}

function CaseStudies() {
  const { ref, inView } = useInView();
  const [studies] = useState<CaseStudyData[]>(FALLBACK_CASE_STUDIES);

  const getBrand = (id: string) => CASE_STUDY_BRANDS.find(b => b.id === id);

  return (
    <section id="case-studies" className="bg-[#0f0f0f] py-24 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div>
              <h2 className="font-display font-black text-white leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
                Numbers don&apos;t lie.
                <br />
                <span className="text-[#7C3AED]">Ours don&apos;t either.</span>
              </h2>
            </div>
            <p className="text-gray-400 max-w-sm text-[15px] leading-relaxed">Real results from real brands. No vanity metrics, no cherry-picking.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {studies.map((study, i) => {
              const brand = getBrand(study.id);
              return (
                <div key={study.id}
                  className={`group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#7C3AED]/40 rounded-2xl p-6 flex flex-col transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                  style={{ transitionDelay: `${i * 120}ms`, transitionProperty: 'opacity, transform, background, border-color' }}>
                  {/* Brand identity */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                      style={{ background: brand?.fallbackColor || '#7C3AED' }}>
                      {study.title.charAt(0)}
                    </div>
                    <div>
                      <div className="text-white font-bold text-[13px]">{study.title}</div>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {study.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="text-[9px] font-bold text-[#7C3AED] bg-[#7C3AED]/10 px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Metric */}
                  <div className="text-[1.8rem] font-black text-white leading-none mb-3 font-display">{study.metric}</div>

                  {/* Summary */}
                  <p className="text-gray-400 text-[0.82rem] leading-relaxed flex-1 mb-5">{study.summary}</p>

                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── Why Us ───────────────────────────────────────────────────────────────────
function WhyUs({ copy }: { copy: typeof ORIGINAL_COPY }) {
  const { ref, inView } = useInView();

  return (
    <section id="why-us" className="bg-white py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-16">
            <span className="text-[11px] font-bold text-[#7C3AED] tracking-widest uppercase block mb-4">Why Us</span>
            <h2 className="font-display font-black text-[#0f0f0f] leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
              {copy.whyTitle}
            </h2>
          </div>

          {/* Asymmetric bento */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Large card */}
            <div className={`lg:col-span-2 bg-[#1E2A5E] rounded-3xl p-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-2xl mb-6">🎯</div>
              <h3 className="font-display font-black text-white text-[1.4rem] mb-4 leading-snug">{copy.why1Title}</h3>
              <p className="text-white/60 text-[0.95rem] leading-relaxed">{copy.why1Body}</p>
            </div>

            {/* Small card */}
            <div className={`bg-[#EDE9FE] rounded-3xl p-8 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
              <div className="w-12 h-12 rounded-2xl bg-[#7C3AED]/20 flex items-center justify-center text-2xl mb-6">🔍</div>
              <h3 className="font-display font-black text-[#1E2A5E] text-[1.2rem] mb-4 leading-snug">{copy.why2Title}</h3>
              <p className="text-[#1E2A5E]/60 text-[0.9rem] leading-relaxed">{copy.why2Body}</p>
            </div>

            {/* Full width card */}
            <div className={`lg:col-span-3 bg-[#0f0f0f] rounded-3xl p-10 flex flex-col lg:flex-row items-start lg:items-center gap-8 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-2xl flex-shrink-0">🌍</div>
              <div className="flex-1">
                <h3 className="font-display font-black text-white text-[1.2rem] mb-3">{copy.why3Title}</h3>
                <p className="text-white/50 text-[0.9rem] leading-relaxed max-w-2xl">{copy.why3Body}</p>
              </div>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-full transition-all duration-300 text-[13px] whitespace-nowrap">
                Work With Us →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Who This Is For ──────────────────────────────────────────────────────────
function WhoThisIsFor() {
  const { ref, inView } = useInView();

  const forYou = [
    "D2C or B2C brand spending $10K–$50K/month on ads",
    "Selling physical products via Shopify or WooCommerce",
    "Operating in or selling into US, UK, or Australia",
    "Burned by agencies before and want real accountability",
    "Ready to fix tracking before scaling",
  ];
  const notForYou = [
    "Spending under $5K/month (we can't move the needle)",
    "Looking for a \'set it and forget it\' agency",
    "Shopping for the cheapest option",
    "No product-market fit yet",
    "Not willing to fix measurement first",
  ];

  return (
    <section id="who-this-is-for" className="bg-[#f8f7ff] py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-16">
            <span className="text-[11px] font-bold text-[#7C3AED] tracking-widest uppercase block mb-4">Fit Check</span>
            <h2 className="font-display font-black text-[#0f0f0f] leading-tight" style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
              Is this for you?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`bg-white rounded-3xl p-8 border border-green-100 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '100ms' }}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4m0 0l-4 4m4-4H3" /></svg>
                </div>
                <h3 className="font-display font-black text-[#0f0f0f] text-[1.1rem]">This is for you if:</h3>
              </div>
              <ul className="space-y-4">
                {forYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4m0 0l-4 4m4-4H3" /></svg>
                    </span>
                    <span className="text-gray-600 text-[0.9rem] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`bg-white rounded-3xl p-8 border border-red-50 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-7">
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                </div>
                <h3 className="font-display font-black text-[#0f0f0f] text-[1.1rem]">This is NOT for you if:</h3>
              </div>
              <ul className="space-y-4">
                {notForYou.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                    </span>
                    <span className="text-gray-600 text-[0.9rem] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────────────
function FinalCTA({ copy }: { copy: typeof ORIGINAL_COPY }) {
  const { ref, inView } = useInView();

  return (
    <section id="final-cta" className="bg-[#0a0a0a] py-28 relative overflow-hidden">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      {/* Accent glow — top left */}
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-20"
        style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }} />

      {/* Accent glow — bottom right */}
      <div className="absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full opacity-10"
        style={{ background: 'radial-gradient(circle, #a78bfa 0%, transparent 70%)' }} />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative z-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

          {/* Top label */}
          <div className="mb-10">
            <span className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.2em] uppercase text-[#7C3AED] border border-[#7C3AED]/30 bg-[#7C3AED]/10 px-4 py-2 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#7C3AED] animate-pulse inline-block" />
              Free Discovery Call
            </span>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left — headline + body */}
            <div>
              <h2 className="font-display font-black text-white leading-[1.05] mb-8"
                style={{ fontSize: 'clamp(2.6rem, 5vw, 4.2rem)', letterSpacing: '-0.02em' }}>
                Are Your Ads<br />
                <span className="text-[#7C3AED]">Leaving Money</span><br />
                on the Table?
              </h2>

              <p className="text-white/55 text-[1.05rem] leading-relaxed mb-6 max-w-md">
                {copy.ctaSub}
              </p>

            </div>

            {/* Right — CTA card */}
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-3xl p-8 lg:p-10 backdrop-blur-sm">

              {/* Trust signals */}
              <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-b border-white/[0.08]">
                {[
                  { value: '45', unit: 'min', label: 'Free call' },
                  { value: '0', unit: '$', label: 'No commitment' },
                  { value: '48', unit: 'hr', label: 'Audit turnaround' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="font-black text-white leading-none mb-1"
                      style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)' }}>
                      {stat.value}<span className="text-[#7C3AED] text-[0.7em]">{stat.unit}</span>
                    </div>
                    <div className="text-white/35 text-[0.72rem] font-medium tracking-wide uppercase">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* What you get */}
              <ul className="space-y-3 mb-8">
                {[
                  'Full account audit — Meta, Google, GA4',
                  '3–5 actionable findings, written report',
                  'Clear roadmap, whether you hire us or not',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/65 text-[0.9rem]">
                    <span className="w-5 h-5 rounded-full bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 text-[#7C3AED]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* CTA button */}
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
                className="group flex items-center justify-between w-full px-7 py-5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-black rounded-2xl transition-all duration-300 text-[15px] shadow-lg shadow-purple-900/40 hover:shadow-purple-900/60 hover:-translate-y-0.5">
                <span>Book Your Free Audit Call</span>
                <span className="w-9 h-9 rounded-xl bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-all duration-300 group-hover:translate-x-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </a>

              <p className="text-white/25 text-[11px] font-medium text-center mt-4 tracking-wide">
                No contracts · No retainers until we prove the ROI
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function SiteFooter() {
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="bg-[#0f0f0f] py-12 px-6 lg:px-10">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 pb-8 border-b border-white/10">
          <div className="bg-[#1a1a1a] rounded-lg p-2 inline-flex">
            <Image src="/assets/images/TheDesignTurf_Logo_PNG-1775227555414.png" alt="The Design Turf" width={100} height={28} className="h-7 w-auto object-contain" />
          </div>

          <nav className="flex flex-wrap gap-6">
            {[['How We Work', 'how-we-work'], ['Case Studies', 'case-studies'], ['Why Us', 'why-us']].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-[13px] font-medium text-white/40 hover:text-white transition-colors">{label}</button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="https://www.linkedin.com/in/sai-krishna-69bb28112/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
              className="w-9 h-9 flex items-center justify-center border border-white/10 rounded-full text-white/40 hover:text-white hover:border-white/30 transition-all">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6">
          <p className="text-[12px] text-white/20">© 2026 The Design Turf. All rights reserved.</p>
          <p className="text-[12px] text-white/20">iamsaikrishna.official@gmail.com</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote: "Sai's Meta expertise delivered ROAS that exceeded every expectation. He understood our business goals deeply and tailored campaigns to match. Proactive, skilled, and genuinely committed to results — a true asset.",
    name: 'Manhar Oberoi',
    role: 'Founder @ Coral Haze',
    initials: 'MO',
    color: '#E8A598',
  },
  {
    quote: "4 years growing SOQO together — Sai gets the hustle. Impeccable execution, constant communication, results with zero friction. He builds your company like his own. My highest recommendation.",
    name: 'Janvi Tiwari',
    role: 'Founder @ Soqo',
    initials: 'JT',
    color: '#9B59B6',
  },
  {
    quote: "Nearly four years of collaboration and Sai Krishna has been an invaluable asset. His honesty, transparency, and holistic approach set him apart — he goes beyond his core role, maintains the macro view without losing detail, and communicates with clarity that makes every team better. I wholeheartedly endorse him.",
    name: 'Sushanth Nambiar',
    role: 'Founder @ Divya Napa & Co',
    initials: 'SN',
    color: '#3B82F6',
  },
];

function Testimonials() {
  const { ref, inView } = useInView(0.15);
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (index: number) => {
    setVisible(false);
    setTimeout(() => {
      setActive(index);
      setVisible(true);
    }, 300);
  };

  const prev = () => {
    goTo((active - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  const next = () => {
    goTo((active + 1) % TESTIMONIALS.length);
  };

  const startAutoRotate = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setActive(prev => (prev + 1) % TESTIMONIALS.length);
        setVisible(true);
      }, 300);
    }, 5000);
  };

  useEffect(() => {
    startAutoRotate();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleManualNav = (fn: () => void) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    fn();
    startAutoRotate();
  };

  const t = TESTIMONIALS[active];

  return (
    <section className="bg-white py-24">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-14 text-center">
            <span className="text-[11px] font-bold text-[#7C3AED] tracking-widest uppercase block mb-4">Founder Testimonials</span>
            <h2 className="font-display font-black text-[#0f0f0f] leading-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              Straight from the founders.
            </h2>
          </div>

          {/* Carousel */}
          <div className="relative max-w-3xl mx-auto">
            {/* Card */}
            <div
              className="bg-[#f8f7ff] rounded-3xl p-8 lg:p-12 border border-gray-100 flex flex-col gap-6 min-h-[280px]"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            >
              <div className="text-[4rem] leading-none font-black text-[#7C3AED]/20 select-none -mb-4">&ldquo;</div>
              <p className="text-[#1a1a1a] text-[1.05rem] leading-relaxed font-medium flex-1">
                {t.quote}
              </p>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0"
                  style={{ background: t.color }}
                >
                  {t.initials}
                </div>
                <div>
                  <div className="font-bold text-[#0f0f0f] text-[14px]">{t.name}</div>
                  <div className="text-[12px] text-gray-500 font-medium">{t.role}</div>
                </div>
              </div>
            </div>

            {/* Arrow Controls */}
            <button
              onClick={() => handleManualNav(prev)}
              aria-label="Previous testimonial"
              className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED] transition-all duration-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              onClick={() => handleManualNav(next)}
              aria-label="Next testimonial"
              className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED] transition-all duration-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => handleManualNav(() => goTo(i))}
                aria-label={`Go to testimonial ${i + 1}`}
                className="transition-all duration-300 rounded-full"
                style={{
                  width: i === active ? '24px' : '8px',
                  height: '8px',
                  background: i === active ? '#7C3AED' : '#D1D5DB',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DesignTurfLanding() {
  const [copy, setCopy] = useState(ORIGINAL_COPY);
  const [copyLoaded, setCopyLoaded] = useState(false);

  // Fetch Claude-rewritten copy on mount
  useEffect(() => {
    async function loadCopy() {
      try {
        const res = await fetch('/api/rewrite-copy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sections: ORIGINAL_COPY }),
        });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (data.success && data.copy) {
          setCopy({ ...ORIGINAL_COPY, ...data.copy });
        }
      } catch {
        // Keep original copy as fallback
      } finally {
        setCopyLoaded(true);
      }
    }
    loadCopy();
  }, []);

  return (
    <div className="bg-white overflow-x-hidden" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <Navbar />
      <Hero copy={copy} />
      <PainPoints copy={copy} />
      <HowWeWork copy={copy} />
      <CaseStudies />
      <StatsSection />
      <Testimonials />
      <WhyUs copy={copy} />
      <WhoThisIsFor />
      <FinalCTA copy={copy} />
      <SiteFooter />
    </div>
  );
}